import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  computed,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { NgxGraphModule } from '@swimlane/ngx-graph';
import { LeadStatusDto, WorkflowTransitionEdge } from '../../../../shared/services/lead-statuses.service';

const NODE_WIDTH = 180;
const NODE_HEIGHT = 44;
const MAIN_X = 80;
const SIDE_X = 340;
const TOP_Y = 40;
const ROW_GAP = 150;

type WorkflowNode = {
  id: string;
  label: string;
  position: { x: number; y: number };
  dimension: { width: number; height: number };
  data: { isTerminal: boolean };
};

type WorkflowLink = {
  id: string;
  source: string;
  target: string;
  label?: string;
  data?: {
    requiresComment: boolean;
    offsetX: number;
    offsetY: number;
    anchorX: number;
    anchorY: number;
  };
};

@Component({
  selector: 'app-workflow-graph',
  standalone: true,
  imports: [NgxGraphModule],
  template: `
    <div class="workflow-graph-shell" #shell>
      <ngx-graph
        class="workflow-graph"
        [view]="view()"
        [nodes]="nodes()"
        [links]="links()"
        [layout]="'dagreNodesOnly'"
        [layoutSettings]="layoutSettings"
        [nodeWidth]="nodeWidth"
        [nodeHeight]="nodeHeight"
        [enableZoom]="true"
        [autoZoom]="true"
        [autoCenter]="true"
        [centerNodesOnPositionChange]="true"
      >
        <ng-template #defsTemplate>
          <svg:marker
            id="workflow-arrow"
            viewBox="0 -5 10 10"
            refX="8"
            refY="0"
            markerWidth="8"
            markerHeight="8"
            orient="auto"
          >
            <svg:path d="M0,-5L10,0L0,5" fill="#94a3b8"></svg:path>
          </svg:marker>
        </ng-template>

        <ng-template #nodeTemplate let-node>
          <svg:g class="node">
            <svg:rect
              [attr.width]="node.dimension.width"
              [attr.height]="node.dimension.height"
              [attr.rx]="8"
              [attr.ry]="8"
              [attr.fill]="node.data?.isTerminal ? '#f8fafc' : '#ffffff'"
              [attr.stroke]="node.data?.isTerminal ? '#16a34a' : '#cbd5e1'"
              stroke-width="1.5"
            ></svg:rect>
            <svg:text
              alignment-baseline="central"
              [attr.x]="node.dimension.width / 2"
              [attr.y]="node.dimension.height / 2"
              text-anchor="middle"
              fill="#0f172a"
              font-size="13"
              font-weight="600"
            >
              {{ node.label }}
            </svg:text>
          </svg:g>
        </ng-template>

        <ng-template #linkTemplate let-link>
          <svg:g class="edge">
            <svg:path
              [attr.id]="link.id"
              class="line"
              [attr.d]="link.line"
              fill="none"
              [attr.stroke]="link.data?.requiresComment ? '#f59e0b' : '#94a3b8'"
              stroke-width="2"
              marker-end="url(#workflow-arrow)"
            ></svg:path>
            @if (link.label) {
              <svg:text
                class="edge-label"
                [attr.x]="(link.midPoint?.x ?? link.data?.anchorX ?? 0) + (link.data?.offsetX ?? 0)"
                [attr.y]="(link.midPoint?.y ?? link.data?.anchorY ?? 0) + (link.data?.offsetY ?? 0)"
                text-anchor="middle"
                dominant-baseline="middle"
                [attr.fill]="link.data?.requiresComment ? '#b45309' : '#64748b'"
                font-size="12"
              >
                {{ link.label }}
              </svg:text>
            }
          </svg:g>
        </ng-template>
      </ngx-graph>
    </div>
  `,
  styles: `
    .workflow-graph-shell {
      width: 100%;
      height: 62vh;
      min-height: 420px;
      border: 1px solid var(--cg-color-border);
      border-radius: var(--cg-radius-lg);
      overflow: hidden;
      background: color-mix(in srgb, var(--cg-color-bg) 96%, white);
    }

    .workflow-graph {
      width: 100%;
      height: 100%;
      display: block;
    }

    :host ::ng-deep .edge-label {
      paint-order: stroke fill;
      stroke: #ffffff;
      stroke-width: 4px;
      stroke-linejoin: round;
    }
  `,
})
export class WorkflowGraphComponent implements AfterViewInit, OnDestroy {
  readonly statuses = input.required<LeadStatusDto[]>();
  readonly transitions = input.required<WorkflowTransitionEdge[]>();

  readonly shellRef = viewChild<ElementRef<HTMLElement>>('shell');
  readonly view = signal<[number, number]>([800, 520]);

  readonly nodeWidth = NODE_WIDTH;
  readonly nodeHeight = NODE_HEIGHT;
  readonly layoutSettings = {
    orientation: 'TB',
    marginX: 40,
    marginY: 40,
    curveDistance: 24,
    dragEdgeStyle: 'smooth' as const,
  };

  private resizeObserver?: ResizeObserver;

  readonly nodes = computed<WorkflowNode[]>(() => {
    const statuses = [...this.statuses()].sort((a, b) => a.displayOrder - b.displayOrder);
    const statusById = new Map(statuses.map((s) => [s._id, s]));
    const outgoing = new Set(this.transitions().map((t) => t.source));
    const sideIds = this.sideBranchStatusIds(statuses, statusById);

    return statuses.map((s) => {
      const position = sideIds.has(s._id)
        ? this.sideNodePosition(s._id, statusById)
        : { x: MAIN_X, y: TOP_Y + (s.displayOrder - 1) * ROW_GAP };

      return {
        id: s._id,
        label: s.name,
        position,
        dimension: { width: NODE_WIDTH, height: NODE_HEIGHT },
        data: { isTerminal: !outgoing.has(s._id) },
      };
    });
  });

  readonly links = computed<WorkflowLink[]>(() => {
    const nodeById = new Map(this.nodes().map((n) => [n.id, n]));
    const transitions = this.transitions();

    type EdgeMeta = {
      transition: WorkflowTransitionEdge;
      source: WorkflowNode;
      target: WorkflowNode;
      kind: 'forward' | 'backward' | 'side';
    };

    const metas: EdgeMeta[] = [];
    for (const transition of transitions) {
      const source = nodeById.get(transition.source);
      const target = nodeById.get(transition.target);
      if (!source || !target) continue;
      metas.push({
        transition,
        source,
        target,
        kind: this.edgeKind(source, target),
      });
    }

    const sourceKindCounts = new Map<string, number>();
    const sourceKindIndex = new Map<string, number>();
    for (const meta of metas) {
      const key = `${meta.transition.source}:${meta.kind}`;
      const index = sourceKindCounts.get(key) ?? 0;
      sourceKindIndex.set(meta.transition.id, index);
      sourceKindCounts.set(key, index + 1);
    }

    return metas.map((meta) => {
      const groupKey = `${meta.transition.source}:${meta.kind}`;
      const slotIndex = sourceKindIndex.get(meta.transition.id) ?? 0;
      const slotCount = sourceKindCounts.get(groupKey) ?? 1;
      const anchor = this.edgeLabelAnchor(meta.source, meta.target, meta.kind, slotIndex, slotCount);
      const offset = this.edgeLabelOffset(meta.source, meta.target, meta.kind, slotIndex, slotCount);

      return {
        id: `edge-${meta.transition.id}`,
        source: meta.transition.source,
        target: meta.transition.target,
        label: meta.transition.transitionName,
        data: {
          requiresComment: meta.transition.requiresComment,
          offsetX: offset.x,
          offsetY: offset.y,
          anchorX: anchor.x,
          anchorY: anchor.y,
        },
      };
    });
  });

  ngAfterViewInit(): void {
    this.observeShellSize();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateViewFromShell();
  }

  private observeShellSize(): void {
    const shell = this.shellRef()?.nativeElement;
    if (!shell || typeof ResizeObserver === 'undefined') {
      this.updateViewFromShell();
      return;
    }

    this.resizeObserver = new ResizeObserver(() => this.updateViewFromShell());
    this.resizeObserver.observe(shell);
    this.updateViewFromShell();
  }

  private updateViewFromShell(): void {
    const shell = this.shellRef()?.nativeElement;
    if (!shell) return;

    const width = Math.max(640, Math.floor(shell.clientWidth));
    const height = Math.max(400, Math.floor(shell.clientHeight));
    this.view.set([width, height]);
  }

  /** Statuses reached by forward skip edges (e.g. DISQUALIFIED) render in a side column. */
  private sideBranchStatusIds(
    statuses: LeadStatusDto[],
    statusById: Map<string, LeadStatusDto>,
  ): Set<string> {
    const ordered = [...statuses].sort((a, b) => a.displayOrder - b.displayOrder);
    const nextInOrder = new Map<string, string>();
    for (let i = 0; i < ordered.length - 1; i += 1) {
      nextInOrder.set(ordered[i]._id, ordered[i + 1]._id);
    }

    const sideIds = new Set<string>();
    for (const s of statuses) {
      if (s.code === 'DISQUALIFIED') sideIds.add(s._id);
    }

    for (const t of this.transitions()) {
      const from = statusById.get(t.source);
      const to = statusById.get(t.target);
      if (!from || !to) continue;
      if (to.displayOrder > from.displayOrder && nextInOrder.get(from._id) !== to._id) {
        sideIds.add(to._id);
      }
    }

    return sideIds;
  }

  private sideNodePosition(statusId: string, statusById: Map<string, LeadStatusDto>): { x: number; y: number } {
    const incoming = this.transitions().filter((t) => t.target === statusId);
    const sourceOrders = incoming
      .map((t) => statusById.get(t.source)?.displayOrder)
      .filter((order): order is number => order !== undefined);

    const status = statusById.get(statusId);
    const avgOrder =
      sourceOrders.length > 0
        ? sourceOrders.reduce((sum, order) => sum + order, 0) / sourceOrders.length
        : (status?.displayOrder ?? 1);

    return {
      x: SIDE_X,
      y: TOP_Y + (avgOrder - 1) * ROW_GAP,
    };
  }

  private edgeKind(source: WorkflowNode, target: WorkflowNode): 'forward' | 'backward' | 'side' {
    const sourceCenterX = source.position.x + NODE_WIDTH / 2;
    const targetCenterX = target.position.x + NODE_WIDTH / 2;
    if (Math.abs(targetCenterX - sourceCenterX) > 50) return 'side';
    return target.position.y > source.position.y ? 'forward' : 'backward';
  }

  private edgeLabelAnchor(
    source: WorkflowNode,
    target: WorkflowNode,
    kind: 'forward' | 'backward' | 'side',
    slotIndex: number,
    slotCount: number,
  ): { x: number; y: number } {
    const sourceCenterX = source.position.x + NODE_WIDTH / 2;
    const sourceBottomY = source.position.y + NODE_HEIGHT;
    const targetCenterX = target.position.x + NODE_WIDTH / 2;
    const targetTopY = target.position.y;

    const dx = targetCenterX - sourceCenterX;
    const dy = targetTopY - sourceBottomY;

    let t = 0.5;
    switch (kind) {
      case 'forward':
        t = 0.42 - slotIndex * 0.04;
        break;
      case 'backward':
        t = 0.58 + slotIndex * 0.04;
        break;
      case 'side':
        t = 0.45 + (slotCount > 1 ? (slotIndex - (slotCount - 1) / 2) * 0.08 : 0);
        break;
    }

    return {
      x: sourceCenterX + dx * t,
      y: sourceBottomY + dy * t,
    };
  }

  private edgeLabelOffset(
    source: WorkflowNode,
    target: WorkflowNode,
    kind: 'forward' | 'backward' | 'side',
    slotIndex: number,
    slotCount: number,
  ): { x: number; y: number } {
    const sourceCenterX = source.position.x + NODE_WIDTH / 2;
    const sourceBottomY = source.position.y + NODE_HEIGHT;
    const targetCenterX = target.position.x + NODE_WIDTH / 2;
    const targetTopY = target.position.y;

    const dx = targetCenterX - sourceCenterX;
    const dy = targetTopY - sourceBottomY;
    const length = Math.hypot(dx, dy) || 1;
    const fan = slotCount > 1 ? (slotIndex - (slotCount - 1) / 2) * 10 : 0;

    // Small offset perpendicular to the edge so labels sit beside the line, not far from it.
    const perpX = (-dy / length) * 10;
    const perpY = (dx / length) * 10;

    switch (kind) {
      case 'forward':
        return { x: perpX + fan, y: perpY - 2 };
      case 'backward':
        return { x: -perpX + fan, y: -perpY + 2 };
      case 'side':
        return { x: perpX + fan, y: perpY };
    }
  }
}
